import { useState } from "react";
import StepNav, { URINALYSIS_STEPS } from "@/components/StepNav";
import { URINALYSIS_CASES, serveCase } from "@/data/urinalysis";
import { useCaseList } from "@/context/ContentContext";
import { UrinalysisIntro, UrinalysisDebrief } from "@/components/urinalysis/UrinalysisParts";
import {
  GrossBeat,
  RefractometerBeat,
  DipstickBeat,
  SedimentBeat,
  DiagnosisBeat,
} from "@/components/urinalysis/UrinalysisBeats";

const pickRandom = (cases, excludeId) => {
  const pool = cases.filter((c) => c.id !== excludeId);
  const list = pool.length ? pool : cases;
  return list[Math.floor(Math.random() * list.length)];
};

export default function Urinalysis() {
  const cases = useCaseList("urinalysis", URINALYSIS_CASES);
  const [stage, setStage] = useState("intro"); // intro | running | debrief
  const [caseData, setCaseData] = useState(null);
  const [beatIdx, setBeatIdx] = useState(0);
  const [scores, setScores] = useState([]);
  const [debrief, setDebrief] = useState(null);

  const beginCase = (excludeId) => {
    setCaseData(serveCase(pickRandom(cases, excludeId)));
    setBeatIdx(0);
    setScores([]);
    setDebrief(null);
    setStage("running");
  };

  const beatProps = { c: caseData, onDone: handleBeatDone };
  const beats = caseData
    ? [
        <GrossBeat key="gross" {...beatProps} />,
        <RefractometerBeat key="refract" {...beatProps} />,
        <DipstickBeat key="dipstick" {...beatProps} />,
        <SedimentBeat key="sediment" {...beatProps} />,
        <DiagnosisBeat key="diagnosis" {...beatProps} />,
      ]
    : [];

  function handleBeatDone(scoreObj) {
    const next = [...scores, scoreObj];
    if (beatIdx < 4) {
      setScores(next);
      setBeatIdx(beatIdx + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const earned = next.reduce((a, b) => a + b.earned, 0);
    const max = next.reduce((a, b) => a + b.max, 0) || 1;
    setDebrief({
      percent: Math.round((earned / max) * 100),
      breakdown: next,
      caseTitle: caseData.name,
      overview: caseData.diagnosis.overview,
    });
    setStage("debrief");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const currentStep = stage === "running" ? beatIdx + 1 : stage === "debrief" ? 5 : 1;

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <StepNav
        current={currentStep}
        steps={URINALYSIS_STEPS}
        title="Urinalysis"
        subtitle="Cert IV urinalysis trainer"
      />

      <main className="max-w-3xl mx-auto px-6 py-10">
        {stage === "intro" && (
          <UrinalysisIntro caseCount={cases.length} onStart={() => beginCase(null)} />
        )}

        {stage === "running" && caseData && (
          <div data-testid="urinalysis-running">
            <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#b45309] font-semibold">
                  Sample · {caseData.name}
                </div>
                <div className="text-sm text-slate-600">
                  {caseData.breed} · {caseData.signalment} — {caseData.presentation}
                </div>
              </div>
              <div className="flex gap-1.5">
                {URINALYSIS_STEPS.map((s) => (
                  <span
                    key={s.id}
                    className={`w-6 h-1.5 rounded-full ${s.id <= currentStep ? "bg-[#b45309]" : "bg-slate-200"}`}
                  />
                ))}
              </div>
            </div>
            {beats[beatIdx]}
          </div>
        )}

        {stage === "debrief" && debrief && (
          <UrinalysisDebrief
            result={debrief}
            onNext={() => beginCase(caseData.id)}
            onRestart={() => setStage("intro")}
          />
        )}
      </main>
    </div>
  );
}
